import React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  differenceInYears,
  differenceInMonths,
  parse,
  sub,
  isFuture,
  isBefore,
  isAfter,
  isValid,
} from 'date-fns'
import CTA from '../../../common/CTAButton/CTAButton'
import { useStateContext } from '../context/StateContext'
import { componentEvent } from '../../../utils/tracking'
import arrowLeft from '../assets/svgs/arrow-left.svg'
import boy from '../assets/svgs/boy.svg'
import girl from '../assets/svgs/girl.svg'

export default function ChildAge() {
  const [state, setState] = useStateContext()
  const { t } = useTranslation()
  const schema = yup
    .object({
      DOB: yup
        .string()
        .required(t('dob-error'))
        .test('validDateRange', t('dob-error-age'), (value) => {
          const dob = parse(value, 'MM/dd/yyyy', new Date())
          const maxAge = sub(new Date(), { years: 18 })
          const minAge = sub(new Date(), { years: 2 })

          if (
            !isValid(dob) ||
            isFuture(dob) ||
            isBefore(dob, maxAge) ||
            isAfter(dob, minAge)
          ) {
            return false
          }

          return true
        }),
    })
    .required()
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) })

  function calculateAge(data) {
    const dob = parse(data.DOB, 'MM/dd/yyyy', new Date())
    const age = differenceInYears(new Date(), dob)
    const ageInMonths = differenceInMonths(new Date(), dob)

    componentEvent('bmi-calculator', 'content', 'click', 'Submit')

    setState(() => ({
      ...state,
      step: 'child-height-weight',
      age,
      ageInMonths,
    }))
    return age
  }

  function handleBack() {
    componentEvent('bmi-calculator', 'content', 'click', 'Back')

    setState(() => ({
      ...state,
      step: 'child-gender',
      gender: null,
    }))
  }

  return (
    <div className="layout child-age">
      <div className="layout__left">
        <div className="container">
          <h4>{state.gender === 'm' ? t('boy') : t('girl')}</h4>
          <form onSubmit={handleSubmit(calculateAge)}>
            <fieldset>
              <label htmlFor="DOB">{t('dob')}</label>
              <Controller
                control={control}
                name="DOB"
                render={({ field: { onChange, name, value } }) => (
                  <NumberFormat
                    format="##/##/####"
                    placeholder="MM/DD/YYYY"
                    mask={['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y']}
                    className={`input ${errors.DOB ? 'error' : ''}`}
                    displayType="input"
                    name={name}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              {errors.DOB && (
                <div className="error-message">
                  <p>{errors.DOB.message}</p>
                </div>
              )}
            </fieldset>
            <fieldset>
              <CTA className="cta" type="submit">
                {t('submit')}
              </CTA>
            </fieldset>
          </form>
        </div>
      </div>
      <div className="layout__right">
        <div className="container">
          <img
            className="figure"
            src={state.gender === 'm' ? boy : girl}
            alt=""
          />
          <div className="action">
            <h3>
              {t('calculate-a')}
              <span>{t('child')}</span>
            </h3>
            <CTA className="cta cta__arrow" onClick={() => handleBack()}>
              <img src={arrowLeft} alt="" />
            </CTA>
          </div>
        </div>
      </div>
    </div>
  )
}
